import NewGeneratePanel from "@/pages/Posts/Generate/GeneratePanel";
import PostEditorModal from "@/pages/Posts/PostEditor/PostEditor/PostEditorModal";
import { EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Dropdown, Tooltip } from "antd";
import { ItemType } from "antd/es/menu/interface";
import classNames from "classnames";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { FAST_FADE_IN_ANIMATION } from "fixtures/motionAnimations";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import {
  MdOutlineChecklist,
  MdOutlineDelete,
  MdOutlineTune,
} from "react-icons/md";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { Post, PostScreen } from "store/posts/postConstants";
import useDownloadPostsMenuItems from "../../utils/useDownloadPostsMenuItems";
import { POST_SCREEN_TAB } from "../../utils/useGetAllowedScreens";
import LayoutSwitcher, { LayoutView } from "./LayoutSwitcher";
import PostScreenSelector from "./PostScreenSelector";
import ReviewStatusFilter from "./ReviewStatusFilter";

interface PostsPageHeaderProps {
  postList: Post[];
  setSearchParams: SetURLSearchParams;
  downloadScreens?: PostScreen[];
  layout: LayoutView;
  setLayout: (layout: LayoutView) => void;
  allPosts: Post[];
  setFilteredPosts: (posts: Post[]) => void;
  enableReviewStatusFilter: boolean;
  showLayoutSwitcher: boolean;
  isSelectionMode: boolean;
  toggleSelectionMode: () => void;
}

const PostsPageHeader = ({
  postList,
  setSearchParams,
  downloadScreens,
  layout,
  setLayout,
  allPosts,
  setFilteredPosts,
  enableReviewStatusFilter,
  showLayoutSwitcher,
  isSelectionMode,
  toggleSelectionMode,
}: PostsPageHeaderProps) => {
  const { hasPermission } = useUserPermissions();
  const [isDownloading, setIsDownloading] = useState(false);
  const { downloadItem } = useDownloadPostsMenuItems({
    postList,
    downloadScreens,
    setIsDownloading,
  });
  const [showPostEditor, setShowPostEditor] = useState(false);
  const [showGeneratePanel, setShowGeneratePanel] = useState(false);
  const [searchParams] = useSearchParams();

  const currentScreen = searchParams.get(POST_SCREEN_TAB);
  const disableSelect =
    currentScreen === "published" || currentScreen === "scheduled";

  const showDesignButton = hasPermission("post_design_write");
  const showGenerateButton = hasPermission("post_status_generate_write");

  const VIEW_TRASH_ITEM: ItemType = {
    key: "trash",
    label: "View Trash",
    icon: <MdOutlineDelete size={16} />,
    onClick: () => setSearchParams({ [POST_SCREEN_TAB]: "rejected" }),
  };

  const additionalItems = hasPermission("post_status_trash_read")
    ? VIEW_TRASH_ITEM
    : undefined;

  const dropdownItems = useMemo(
    () =>
      additionalItems ? [downloadItem].concat(additionalItems) : [downloadItem],
    [downloadItem, additionalItems]
  );

  return (
    <div className="md:sticky top-0 z-10 bg-background py-2 px-4 flex flex-col md:flex-row md:justify-between items-center gap-2 md:gap-4 border-b border-antd-colorBorder bg-antd-colorBgLayout">
      {/* PostScreenSelector: Mobile Row 1 / Desktop Middle Element */}
      <div className="w-full md:w-auto flex justify-center order-1 md:order-2">
        <PostScreenSelector setSearchParams={setSearchParams} />
      </div>
      {/* Wrapper for Mobile Row 2; its children integrate into desktop layout via md:contents */}
      <div className="flex flex-row justify-between items-center w-full order-2 md:contents gap-2">
        {/* Bulk actions & Filter: Mobile Row 2 Left / Desktop Far Left */}
        <div className="flex items-center gap-2 w-auto justify-start md:order-1">
          <div className="hidden md:block">
            <LayoutSwitcher
              layout={layout}
              setLayout={setLayout}
              disabled={!showLayoutSwitcher}
            />
          </div>
          <Tooltip
            title={
              disableSelect
                ? "Cannot select posts on Published or Scheduled tabs"
                : isSelectionMode
                  ? "Exit Selection Mode"
                  : "Select Posts"
            }
          >
            <Button
              size="small"
              icon={<MdOutlineChecklist size={16} />}
              onClick={toggleSelectionMode}
              type={isSelectionMode ? "primary" : "default"}
              disabled={disableSelect}
            />
          </Tooltip>
          <ReviewStatusFilter
            disabled={!enableReviewStatusFilter}
            {...{ allPosts, setFilteredPosts }}
          />
        </div>

        {/* Action Buttons: Mobile Row 2 Right / Desktop Far Right */}
        <motion.div
          {...FAST_FADE_IN_ANIMATION}
          className={classNames(
            "flex items-center gap-2 w-auto justify-end md:order-3 md:justify-start"
          )}
        >
          {showDesignButton && (
            <Button
              icon={<PlusOutlined size={18} />}
              size="small"
              onClick={() => setShowPostEditor(true)}
            >
              <span className="max-lg:hidden">New Post</span>
            </Button>
          )}
          {showGenerateButton && (
            <Tooltip title="Generate Options">
              <Button
                icon={<MdOutlineTune size={18} />}
                size="small"
                onClick={() => setShowGeneratePanel(true)}
              />
            </Tooltip>
          )}
          <Dropdown
            disabled={isDownloading}
            menu={{
              className: "min-w-max w-[150px]",
              items: dropdownItems,
            }}
          >
            <Button
              loading={isDownloading}
              size="small"
              icon={<EllipsisOutlined />}
            />
          </Dropdown>
        </motion.div>
      </div>
      {/* End of wrapper for Mobile Row 2 */}
      <NewGeneratePanel
        open={showGeneratePanel}
        onCancel={() => setShowGeneratePanel(false)}
        onSubmit={() => setShowGeneratePanel(false)}
      />
      <PostEditorModal
        isOpen={showPostEditor}
        closeEditor={() => setShowPostEditor(false)}
        refetchAfterSchedule
      />
    </div>
  );
};

export default PostsPageHeader;
